import { computed, toValue } from 'vue'
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData
} from '@tanstack/vue-query'
import type { MaybeRefOrGetter } from 'vue'

import type { ReadOptions } from './types'

/**
 * `useList` / `useItem` / `useCreate` / `useUpdate` / `useDelete` over one entity's api
 * module and query keys — an entity's `api/queries.ts` is this call and nothing else.
 *
 * Hand it only the calls the entity has; a hook whose call was not passed rejects instead
 * of failing quietly. What the hooks do on their own:
 *
 * - `useList` takes its params as a ref or getter, re-queries when they change, keeps the
 *   previous page on screen while the next one loads, and returns `items` and `total`
 *   already unwrapped from the v2 envelope.
 * - `useItem` stays disabled until its id is truthy, so it can be bound to a route param.
 * - `useUpdate` is created **for one id** and its `mutate` takes the payload alone.
 * - every mutation invalidates the entity's keys (plus `alsoInvalidate`) on success, so no
 *   caller refreshes a list by hand.
 *
 * The type parameters are separate because the calls genuinely differ: `TItem` is what the
 * list returns and `TDetail` what one record returns, and an update usually omits the
 * fields fixed at creation.
 */
export function createEntityQueries<
  TListParams,
  TItem,
  TCreatePayload = never,
  TUpdatePayload = TCreatePayload,
  TDetail = TItem,
  // Inferred from the functions actually passed, so a mutation's response keeps the shape
  // its api module gave it. Do not widen them to `any`: a create modal reading an id off
  // the response would then be checking nothing.
  TCreateResponse = unknown,
  TUpdateResponse = unknown,
  TRemoveResponse = unknown
>(config: {
  keys: {
    all: readonly unknown[]
    list: (params: TListParams) => readonly unknown[]
    detail: (id: string | number) => readonly unknown[]
  }

  // Optional, so a read that cannot be aborted still fits — see `ReadOptions`.
  getItems?: (
    params: TListParams,
    options?: ReadOptions
  ) => Promise<{ data: { data: TItem[]; meta?: { total?: number } } }>
  getItem?: (
    id: string | number,
    options?: ReadOptions
  ) => Promise<{ data: { data: TDetail } }>

  create?: (payload: TCreatePayload) => Promise<TCreateResponse>
  update?: (args: {
    id: string | number
    data: TUpdatePayload
  }) => Promise<TUpdateResponse>
  remove?: (id: string | number) => Promise<TRemoveResponse>

  /**
   * Extra query keys every mutation invalidates, on top of `keys.all` — for an entity whose
   * record is also displayed by a *neighbouring* list, which would otherwise keep showing
   * the old copy. Declare it here, beside the entity, so no screen that mutates has to
   * remember the second list.
   */
  alsoInvalidate?: readonly (readonly unknown[])[]
}) {
  /**
   * The stand-in for a call the entity did not hand in. All five hooks are always built, so
   * the refusal has to come from *using* one — and as a rejection rather than a throw at
   * setup, so it lands in the caller's own error handling like any other failed request.
   */
  const notImplemented = (call: string) =>
    Promise.reject(new Error(`${call} is not implemented for this entity`))

  function useList(
    params: MaybeRefOrGetter<TListParams>,
    listOptions?: { enabled?: MaybeRefOrGetter<boolean> }
  ) {
    const paramsValue = computed(() => toValue(params))
    const enabled = computed(() =>
      listOptions?.enabled === undefined ? true : toValue(listOptions.enabled)
    )
    const query = useQuery({
      queryKey: computed(() => config.keys.list(paramsValue.value)),
      queryFn: ({ signal }) =>
        config.getItems
          ? config.getItems(paramsValue.value, { signal })
          : notImplemented('getItems'),
      placeholderData: keepPreviousData,
      enabled
    })

    const items = computed(() => {
      return query.data.value?.data?.data ?? []
    })
    const total = computed(() => {
      return query.data?.value?.data?.meta?.total ?? 0
    })

    return {
      ...query,
      items,
      total
    }
  }

  function useItem(id: MaybeRefOrGetter<string | number | undefined>) {
    const idValue = computed(() => toValue(id))

    const query = useQuery({
      queryKey: computed(() => config.keys.detail(idValue.value!)),
      queryFn: ({ signal }) =>
        config.getItem
          ? config.getItem(idValue.value!, { signal })
          : notImplemented('getItem'),
      enabled: computed(() => !!idValue.value)
    })

    const item = computed(() => {
      return query.data.value?.data?.data ?? null
    })

    return {
      ...query,
      item
    }
  }

  function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
    qc.invalidateQueries({ queryKey: config.keys.all })
    for (const queryKey of config.alsoInvalidate ?? [])
      qc.invalidateQueries({ queryKey })
  }

  function useCreate() {
    const qc = useQueryClient()

    return useMutation({
      mutationFn: (payload: TCreatePayload) =>
        config.create ? config.create(payload) : notImplemented('create'),
      onSuccess() {
        invalidateAll(qc)
      }
    })
  }

  function useUpdate(id: MaybeRefOrGetter<string | number | undefined>) {
    const idValue = computed(() => toValue(id))
    const qc = useQueryClient()

    return useMutation({
      mutationFn: (data: TUpdatePayload) => {
        const resolvedId = idValue.value

        if (!resolvedId) {
          throw new Error('ID is required for update')
        }

        return config.update
          ? config.update({ id: resolvedId, data })
          : notImplemented('update')
      },

      onSuccess() {
        invalidateAll(qc)
      }
    })
  }

  function useDelete() {
    const qc = useQueryClient()

    return useMutation({
      mutationFn: (id: string | number) =>
        config.remove ? config.remove(id) : notImplemented('remove'),
      onSuccess() {
        invalidateAll(qc)
      }
    })
  }

  return {
    useList,
    useItem,
    useCreate,
    useUpdate,
    useDelete
  }
}
